/* eslint-disable react/prop-types */
/* eslint-disable camelcase */
/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { makeStyles } from '@material-ui/core/styles';
import { Card, CardMedia, CardContent, Typography } from '@material-ui/core';
import { MoreVert } from '@material-ui/icons';
import CustomMenu from '../menu';
import renderIcons from './renderIcons';

// styles
import { whiteAlpha, blackAlpha } from '../../styles/utils';
import { formatDateTime } from '../../helperFunctions';
import { Text } from '../../styles/common';
import { colors, blur } from '../../styles/variables';
import {
  UPLOAD_STATUS,
  UPDATED_ASSET_STATUS,
  TRANS_STATUS,
} from '../../constants';
import { StatusStyles } from '../../storyboard/components/CollaboratePermissions/Collaborate';

// Components
import Tooltip from '../tooltip';

const CenterStyles = css`
  top: 50%;
  left: 50%;
  width: 100%;
  position: absolute;
  text-align: center;
  transform: translate(-50%, -50%);
`;

const AssetStatusStyles = styled.div`
  ${CenterStyles};
`;

const CardStyled = styled(Card)`
  position: relative;
  animation-name: slideUp;
  animation-duration: 0.5s;

  .asset-status {
    background-color: ${blackAlpha(0.5)};
    backdrop-filter: ${`blur(${blur})`};
    border-radius: 1.5rem;
  }

  .aspect-ratio {
    padding-top: 56.25%;
    position: relative;
    height: auto;
    overflow: hidden;
  }

  .non-playbale-thumbnail {
    .MuiIcon-root,
    img {
      ${CenterStyles};
      font-size: 4rem;
      color: #1d4376;
    }

    img {
      width: auto;
    }
  }

  .info-styles {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 1rem;

    p {
      padding: 0;
      color: ${whiteAlpha(0.7)};
    }

    .MuiIcon-root {
      font-size: 1.8rem;
      color: ${whiteAlpha(0.7)};
    }

    .date {
      font-size: 1rem;
    }

    .version-icon {
      display: flex;
      align-items: center;

      > p {
        padding-right: 0.5rem;
        font-size: 1.4rem;
      }
    }
  }

  .iframe-image-block {
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    cursor: pointer;
    overflow: hidden;
    background: #000;
    opacity: 0;
    &:hover {
      opacity: 1;
    }
    .hover-pointer {
      position: absolute;
      width: 1px;
      height: 100%;
      top: 0;
      background: #fff;
      pointer-events: none;
      z-index: 1;
    }
    img {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: auto;
    }
  }

  @keyframes slideUp {
    from {
      transform: translateY(2rem);
      opacity: 0;
    }
    to {
      transform: translateY(0rem);
      opacity: 1;
    }
  }
`;

const useStyles = makeStyles({
  root: {
    // width: '19.4rem',
    position: 'relative',
    backgroundColor: 'transparent',
    margin: '0.75rem',
    cursor: ({ trans_status, upload_status }) =>
      trans_status !== TRANS_STATUS.finished ||
      upload_status !== UPLOAD_STATUS.finished
        ? 'initial'
        : 'pointer',
    pointerEvents: ({ upload_status, percentage, userDetails, created_by }) => {
      if (
        upload_status === UPLOAD_STATUS.inprogress &&
        (percentage || userDetails.user_id !== created_by)
      ) {
        return 'none';
      }
      return 'initial';
    },
    '& h3': {
      fontWeight: 100,
      padding: '0%',
      fontSize: '1.4rem',
    },
    '&:hover': {
      '& .options': {
        display: 'flex',
      },

      '& .hover-none': {
        display: 'none',
      },
    },
    '& .options': {
      position: 'absolute',
      right: '1rem',
      top: '1.1rem',
      width: '2.5rem',
      height: '2.5rem',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: '100%',
      backgroundColor: blackAlpha(0.5),
      backdropFilter: `blur(${blur})`,
      display: 'none',
      transition: 'opacity 0.25s ease',
      '&:hover': {
        background: '#2c86ff',
      },
      '& .MuiSvgIcon-root': {
        fill: '#fff',
        fontSize: '1.8rem',
      },
    },
  },
  gradientBackground: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.4rem',
    color: '#1d4376',
    background: '#ffecb3', // (props) => props.cardColor,
  },
  loadingMessageWrapper: {
    textAlign: 'center',
  },
  precentage: {
    fontSize: '4rem',
    padding: 0,
    fontWeight: 700,
    color: '#1d4376',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingMessage: {
    padding: 0,
  },
  errorMessage: {
    padding: 0,
  },
  sign: {
    fontSize: '2rem',
    fontWeight: 400,
    color: '#1d4376',
  },
  preview: {
    position: 'relative',
    backgroundColor: ({ thumbnail, background_hex }) =>
      !thumbnail && background_hex,
  },
  cardInfo: {
    cursor: ({ trans_status, upload_status }) =>
      trans_status !== TRANS_STATUS.finished ||
      upload_status !== UPLOAD_STATUS.finished
        ? 'initial'
        : 'pointer',
    borderRadius: '0 0 0.4rem 0.4rem',
    padding: '0',
    margin: '0',
    backgroundColor: '#0c1d33',
    paddingBottom: '0 !important',
    minHeight: '6rem',
  },
  cardTitle: {
    color: '#fff',
    paddingBottom: '0.5rem',
    fontSize: '1.2rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  optionsParent: {
    display: 'flex',
    alignItems: 'center',
    position: 'absolute',
    right: 0,
    top: 0,
    width: '100%',
    justifyContent: 'space-between',
    padding: '1rem',
    columnGap: '1rem',
  },
});

export default function AssetCard(props) {
  const classes = useStyles(props);
  const {
    thumbnail,
    ingest_db_id,
    title,
    folder_name,
    upload_status,
    trans_status,
    errorMessage,
    error,
    status,
    uploaded_date,
    percentage,
    etaTime,
    // version,
    file_type,
    onCardClick,
    menuListArr,
    onActionClick,
    id,
    onDragStart,
    asset_type,
    iframeurl,
    background_hex,
    created_by,
    userDetails,
  } = props;
  let menuOptionsRef;
  let iframeImage;
  let iframeImageBlock;
  let hoverPointer;
  let dragging = false;

  const [menuOpen, setMenuOpen] = useState(false);
  const [dragType, setDragType] = useState('');
  const CARD_TITLE = title || folder_name || 'Card Title';

  const handleClick = event => {
    if (menuOptionsRef && menuOptionsRef.contains(event.target)) {
      setMenuOpen(true);
    } else if (menuOpen) {
      setMenuOpen(false);
    } else if (onCardClick) {
      onCardClick();
    }
  };

  // useEffect(() => {
  //   if (process.env.TYPE === APP_TYPES.app) {
  //     const ipc = require('electron').ipcRenderer;
  //     ipc.on(`drag-end-${id}`, () => {
  //       if (dragType) {
  //         setDragType('');
  //         dragging = false;
  //         onDragStart('');
  //       }
  //     });
  //   }
  // }, []);

  const handleMouseMove = () => {
    if (iframeImageBlock) {
      const iframeBlockHeight = iframeImageBlock.offsetHeight;
      const iframeOgHeight = 240;
      const ogHeight = iframeImage.naturalHeight;
      const { offsetHeight } = iframeImage;
      const { offsetWidth } = iframeImage;
      const iframeCount = ogHeight / iframeOgHeight;
      const iframeOffsetHeight = offsetHeight / iframeCount;
      const imageChangePoint = offsetWidth / iframeCount;
      const offsetTop = Math.abs(iframeBlockHeight - iframeOffsetHeight) / 2;
      hoverPointer.style.left = event.offsetX + 'px';
      const imageNum = parseInt(event.offsetX / imageChangePoint) || 1;
      let imageTop = iframeOffsetHeight * imageNum + offsetTop;
      if (imageTop > offsetHeight) {
        imageTop = offsetHeight + offsetTop - iframeOffsetHeight;
      }
      iframeImage.style.top = '-' + imageTop + 'px';
    }
  };

  const onDrag = event => {
    /*
     * setDragType('internal');
     * dragging = true;
     * onDragStart('internal');
     * const img = document.createElement('img');
     * img.id = `image-${id}`;
     * img.src = thumbnail;
     * img.style.width = '10rem';
     * event.dataTransfer.setDragImage(img, 0, 0);
     * event.dataTransfer.setData('internaldrag', true);
     * if (process.env.TYPE === 'app') {
     *   event.preventDefault();
     *   // eslint-disable-next-line global-require
     *   const ipc = require('electron').ipcRenderer;
     *   ipc.send('ondragstart', filepath, id);
     * }
     */
  };

  const onDragEnd = () => {
    if (dragType) {
      setDragType('');
      dragging = false;
      onDragStart('');
    }
  };

  const renderNonPlayableAsset = () => {
    return (
      <div
        className='aspect-ratio non-playbale-thumbnail'
        style={{ backgroundColor: background_hex }}>
        {renderIcons({ small: false, file_type, title })}
      </div>
    );
  };

  return (
    <CardStyled
      onDragStart={event => onDrag(event)}
      onDragEnd={() => onDragEnd()}
      draggable
      className={classes.root}
      onClick={handleClick}
      id={ingest_db_id}>
      {upload_status === UPLOAD_STATUS.finished &&
      (trans_status === TRANS_STATUS.finished ||
        trans_status === TRANS_STATUS.thumbnailGenerated ||
        trans_status === TRANS_STATUS.previewNotAvailable) ? (
        <>
          {file_type === 'video' || file_type === 'image'
            ? thumbnail && (
                <CardMedia
                  className={classes.preview}
                  image={thumbnail}
                  title={CARD_TITLE}>
                  {trans_status === TRANS_STATUS.finished && iframeurl && (
                    <div
                      onMouseMove={() => handleMouseMove()}
                      className='iframe-image-block'
                      ref={imageBlock => {
                        iframeImageBlock = imageBlock;
                      }}>
                      <div
                        className='hover-pointer'
                        ref={pointer => {
                          hoverPointer = pointer;
                        }}
                      />
                      <img
                        alt={CARD_TITLE}
                        className='iframe-image'
                        ref={image => {
                          iframeImage = image;
                        }}
                        src={iframeurl}
                      />
                    </div>
                  )}
                </CardMedia>
              )
            : renderNonPlayableAsset()}
        </>
      ) : null}

      {upload_status === UPLOAD_STATUS.finished &&
        trans_status === TRANS_STATUS.failed &&
        renderNonPlayableAsset()}

      {upload_status === UPLOAD_STATUS.finished &&
        trans_status !== TRANS_STATUS.failed &&
        trans_status !== TRANS_STATUS.finished &&
        trans_status !== TRANS_STATUS.thumbnailGenerated &&
        trans_status !== TRANS_STATUS.previewNotAvailable && (
          <div className='aspect-ratio' style={{ backgroundColor: '#CFD8DC' }}>
            <AssetStatusStyles>
              <Text color='#1d4376' medium>
                Generating Thumbnail
              </Text>
            </AssetStatusStyles>
          </div>
        )}

      {(upload_status === UPLOAD_STATUS.inprogress || error) && (
        <div className='aspect-ratio' style={{ backgroundColor: '#B2EBF2' }}>
          {upload_status === UPLOAD_STATUS.inprogress && (
            <AssetStatusStyles>
              {percentage || etaTime ? (
                <>
                  <p className={classes.precentage}>
                    {percentage}
                    <span className={classes.sign}>%</span>
                  </p>
                  {etaTime ? (
                    <p className={classes.loadingMessage}>
                      ETA: {etaTime} Mins
                    </p>
                  ) : null}
                </>
              ) : (
                <Text color='#1d4376' medium>
                  {created_by === userDetails?.user_id
                    ? 'Initiating Upload'
                    : 'Initiated by other user'}
                </Text>
              )}
            </AssetStatusStyles>
          )}
          {error && (
            <AssetStatusStyles>
              <Text medium>{errorMessage}</Text>
            </AssetStatusStyles>
          )}
        </div>
      )}

      <div className={classes?.optionsParent}>
        {upload_status === UPLOAD_STATUS.finished &&
          trans_status === TRANS_STATUS.finished &&
          (file_type === 'video' ||
            file_type === 'image' ||
            file_type === 'audio') &&
          status &&
          status !== 'No Status' && (
            <div className='asset-status flex items-center hover-none'>
              <StatusStyles
                style={{ marginRight: '0', marginLeft: '1.2rem' }}
                bgColor={
                  UPDATED_ASSET_STATUS[
                    UPDATED_ASSET_STATUS.findIndex(el => el.label === status)
                  ]?.color
                }
              />
              <Text color={colors.white} xs paddingLeft='0.5rem'>
                {status}
              </Text>
            </div>
          )}
        {upload_status === UPLOAD_STATUS.finished &&
          trans_status !== TRANS_STATUS.finished &&
          (trans_status === TRANS_STATUS.thumbnailGenerated ||
            trans_status === TRANS_STATUS.previewNotAvailable ||
            trans_status === TRANS_STATUS.failed) && (
            <>
              <div className='asset-status'>
                <Text color={colors.white} xs>
                  {trans_status === TRANS_STATUS.thumbnailGenerated &&
                    'Generating Preview'}
                  {(trans_status === TRANS_STATUS.previewNotAvailable ||
                    trans_status === TRANS_STATUS.failed) &&
                    'Preview Not Available'}
                </Text>
              </div>
            </>
          )}

        {asset_type !== 'user_asset' && menuListArr && menuListArr.length && (
          <div
            className='options'
            ref={ref => {
              menuOptionsRef = ref;
            }}>
            <CustomMenu
              onMenuItemClick={item => onActionClick(item)}
              menuList={menuListArr}
              icon={<MoreVert />}
            />
          </div>
        )}
      </div>

      <Tooltip
        title={CARD_TITLE.length > 29 ? CARD_TITLE : ''}
        hide
        placement='top'
        arrow>
        <CardContent className={classes.cardInfo}>
          <Typography
            variant='body1'
            color='textSecondary'
            component='p'
            className={classes.cardTitle}>
            {CARD_TITLE}
          </Typography>

          <div className='info-styles'>
            <p className='date'>{`${formatDateTime(uploaded_date)?.date} | ${
              formatDateTime(uploaded_date)?.time
            }`}</p>
            <div className='version-icon'>
              {/* {version ? (
                <p className={classes.versionNumber}>{version}</p>
              ) : null} */}
              {renderIcons({ small: true, file_type, title })}
            </div>
          </div>
        </CardContent>
      </Tooltip>
    </CardStyled>
  );
}

AssetCard.propTypes = {
  menuListArr: PropTypes.instanceOf(Array),
  onCardClick: PropTypes.func,
  onActionClick: PropTypes.func,
  onDragStart: PropTypes.func,
  errorMessage: PropTypes.string,
  thumbnail: PropTypes.string,
  ingest_db_id: PropTypes.number,
  title: PropTypes.string,
  folder_name: PropTypes.string,
  upload_status: PropTypes.number,
  trans_status: PropTypes.number,
  iframeurl: PropTypes.string,
  background_hex: PropTypes.string,
  uploaded_date: PropTypes.string,
  file_type: PropTypes.string,
  percentage: PropTypes.number,
  // version: PropTypes.number,
  created_by: PropTypes.string,
  status: PropTypes.string,
  userDetails: PropTypes.instanceOf(Object),
};
